<template>
  <ion-page id="lichessopengames-page">
    <ion-content>

      <div v-if="showState == SHOW_EXPLAINTOUR">
        <ExplainTour :screen="explainTourScreen"
                      :wordsSpoken="explainTourWordsSpoken"
                      :wordsSpokenTrigger="explainTourWordsSpokenTrigger"
                      @toolbar-click="explainTourToolbarClick" />
      </div>

      <div v-show="showState == SHOW_LIST"
                  :class="{ 'text-dark' : isDark,'text-light' : !isDark }">
        <TopLine :style="styleTopline" :title="openGamesTitle" :microphoneIsMuted="microphoneIsMuted" @topline-click="toplineClick"/>

        <div :style="styleBoard">
          <CMChessboard :fen="fen" :orientation="orientation" :boardSize="boardSize"  @board-move="boardMove" />
        </div>
  
        <ion-list id="open-game-list" :style="styleGameList">
          <ion-item v-show="gameList.length == 0">
            <ion-label>
              <span v-show="showLoadingGames">{{ translatePhrase("Loading Open Game List") }}</span>
              <span v-show="!showLoadingGames">{{ translatePhrase("No Open Games") }}</span>
            </ion-label>
          </ion-item>

          <ion-item v-for="item in gameList" v-bind:key="item.index" @click="onClick(item.index)"
               class="game-list-center-text"
                   :class="{
                  'selected-text-dark' : (item.selected) && isDark,
                  'selected-text-light' : (item.selected) && !isDark
                }">
            <ion-label>
              "{{ gameString }} {{ item.index+1 }}" {{ item.myTurn }} 
              <span style="color:red;">{{ item.color }} vs {{ item.name }}</span> {{ item.opponentTurn }} {{ item.rated }} {{ item.speed }}
            </ion-label>
          </ion-item>
        </ion-list>
        <ToolBar :style="styleToolbar" :elements="toolbarElements" :trigger="toolbarTrigger" @toolbar-click="toolbarClick" />
      </div>
    </ion-content>
  </ion-page>
  

</template>

<script setup>

  import { IonPage, IonContent,IonList,IonItem,IonLabel  } from '@ionic/vue';
  import { computed } from "@vue/reactivity"
  import CMChessboard from '@/components/CMChessboard.vue'
  import { ref, watch } from 'vue'
  import ExplainTour from '@/components/ExplainTour.vue'
  import ToolBar from '@/components/ToolBar.vue'
  import { useRouter } from 'vue-router';
  import { useConnectionStore } from '@/stores/connectionStore';
  import { doStartupCheck } from '@/composables/startupCheck.js'
  import TopLine from '@/components/TopLine.vue'
  import { useGetData , generateErrorAlertText, getLichessData } from '../../composables/getData.js'
  import { parseWordsSaid , buildWordListFromChoiceList, parseNumberWords } from '@/composables/wordlistSupport.js'

  import { useWindowSizeStore } from '../../stores/windowSizeStore.js';

  import { buildStyleToolbar, buildStyleTopline, availableWidthHeight, scrollElement,
              calculateChessBoardRect, isScreenPortraitOrientation } from '@/composables/screenSize'
import { translateEnglishWords , translatePhrase} from '@/composables/translation.js';

const emit = defineEmits(['where-next','word-list','mic-is-muted'])

  const props = defineProps({
      wordsSaid : {
        type : String,
        default : ""
      },
      wordsSaidTrigger : {
        type : Number,
        default : 0
      }
    })

  const wordsSaid = computed({get() {return props.wordsSaid}})
  const wordsSaidTrigger = computed({get() {return props.wordsSaidTrigger}})

  const router = useRouter()
  const connectionStore = useConnectionStore()
  const windowSizeStore = useWindowSizeStore()

  const lichessAccessToken = connectionStore.getLichessAccessToken
  const isDark = ref(connectionStore.getDarkmode != "light")

  const microphoneIsMuted = ref(connectionStore.getMicrophoneIsMuted)
  emit("mic-is-muted",microphoneIsMuted.value)


  const windowSizeRef = ref({ width : windowSizeStore.getWindowSize['width'],
                        height : windowSizeStore.getWindowSize['height'] })
  
  const orientation = ref("white")
  const INITIAL_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
  const fen = ref(INITIAL_FEN)

  const boardSize = ref(300)

  var scrollCounter = 0
  const explainScrollTrigger = ref(0)

  const explainTourWordsSpoken = ref("")
  const explainTourScreen = ref("")
  const explainTourWordsSpokenTrigger = ref(0)

  const wordlistExplain = "command done"
  const wordlistTrigger = ref(0)

  const wordlistOpenGames = "command scroll up down game go refresh explain done ignore mute listen one two three four five six seven eight nine ten eleven twelve thirteen fourteen fifteen sixteen seventeen eighteen nineteen twenty thirty forty fifty";

  const wordlist = ref(wordlistOpenGames)
  emit("word-list",wordlistOpenGames)
  
  const openGamesTitle = ref(translateEnglishWords("Open Games List"))
  const gameString = ref(translateEnglishWords("Game"))

  const SHOW_LIST = 0
  const SHOW_EXPLAINTOUR = 1
  const showState = ref(SHOW_LIST)
  const showLoadingGames = ref(true)

  const TOOLBAR_GOTO = 0
  const TOOLBAR_REFRESH = 1
  const TOOLBAR_EXPLAIN = 2

  const toolbarElements = ref([
    { index : TOOLBAR_GOTO, label : translateEnglishWords("Go to Game") ,disabled : true},
    { index : TOOLBAR_REFRESH, label : translateEnglishWords("Refresh") ,disabled : false},
    { index : TOOLBAR_EXPLAIN, label : translateEnglishWords("Explain") ,disabled : false},
  ])

  const toolbarTrigger = ref(0)

  var gamelistHeight = 0
  var scrollTop = 0

  var openGameId = ""

  const gameList = ref([])

  const styleGameList = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleBoard = ref({
    position : "absolute",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

   const styleToolbar = ref({
    position : "absolute",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })
 const styleTopline = ref({
    position : "absolute",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  doStartupCheck(false,afterStartupCallbackRoutine)

  function afterStartupCallbackRoutine(stuffChanged) {
    updateWidthHeights()
    getOpenGames()
  }

  function updateStyleObjects(boundaries) {
    boardSize.value = boundaries['board'].height

    styleBoard.value.height = boardSize.value + "px"
    styleBoard.value.height = boundaries['board'].height + "px"
    styleBoard.value.width = boundaries['board'].width + "px"

    styleBoard.value.top = boundaries['board'].top + "px"
    styleBoard.value.left = boundaries['board'].left + "px"

    styleGameList.value.height = boundaries['gamelist'].height + "px"
    styleGameList.value.width = boundaries['gamelist'].width + "px"
    styleGameList.value.top = boundaries['gamelist'].top + "px"
    styleGameList.value.left = boundaries['gamelist'].left + "px"

    //console.log("**** boardsize ",boardSize.value)
  }

  function updateWidthHeights() {

    const availableSpace = availableWidthHeight()
    const isPortrait = isScreenPortraitOrientation(availableSpace)

    styleToolbar.value = buildStyleToolbar(availableSpace)
    styleTopline.value = ref(buildStyleTopline(availableSpace))

    const boundaries = (isPortrait) ? calculatePortraitWidthHeights(availableSpace) :
                                      calculateLandscapeWidthHeights(availableSpace)
    
    updateStyleObjects(boundaries)
  }

  function calculatePortraitWidthHeights(availableSpace) {
    //console.log("available space ",availableSpace)

     const spaceForHeight = availableSpace.height * 0.7
    const availableSpaceForBoard = { width : availableSpace.width ,
              height : spaceForHeight,
              isPortrait : true }

    const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)

    const boardHeight = boardDimensions.height
    const boardWidth = boardDimensions.width
    const boardPositionTop = boardDimensions.y
    const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

    const gamelistPositionTop = boardPositionTop + boardHeight
    const gamelistPositionLeft = availableSpace.leftRightIndent
    gamelistHeight = availableSpace.height - gamelistPositionTop
    const gamelistWidth = availableSpace.width - availableSpace.leftRightIndent * 2

    const result = { board : {
        width : boardWidth,
        height : boardHeight,
        left : boardPositionLeft + availableSpace.leftIndent,
        top : boardPositionTop+ availableSpace.topIndent
      },
      gamelist : {
        width : gamelistWidth,
        height : gamelistHeight,
        left : gamelistPositionLeft + availableSpace.leftIndent,
        top : gamelistPositionTop+ availableSpace.topIndent
      }
    }
    //console.log("board and movelist dimensions",result)
    return (result)
  }

  function calculateLandscapeWidthHeights(availableSpace) {

    //console.log("available space ",availableSpace)

    const spaceForWidth = availableSpace.width * 0.7
    const availableSpaceForBoard = { width : spaceForWidth,
              height : availableSpace.height ,
              isPortrait : false }
    const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)

    const boardHeight = boardDimensions.height
    const boardWidth = boardDimensions.width
    const boardPositionTop = boardDimensions.y
    const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

    const gamelistPositionTop = boardPositionTop
    const gamelistPositionLeft = boardPositionLeft + boardWidth + availableSpace.leftRightIndent
    gamelistHeight = availableSpace.height
    const gamelistWidth = availableSpace.width - gamelistPositionLeft

    const result = { board : {
        width : boardWidth,
        height : boardHeight,
        left : boardPositionLeft + availableSpace.leftIndent,
        top : boardPositionTop + availableSpace.topIndent
      },
      gamelist : {
        width : gamelistWidth,
        height : gamelistHeight,
        left : gamelistPositionLeft + availableSpace.leftIndent,
        top : gamelistPositionTop + availableSpace.topIndent
      }
    }
    return (result)
  }

   watch(wordsSaidTrigger, (newVal,oldVal) => {
     //console.log('*********** words said ',props.wordsSaid)
     processWords(props.wordsSaid)
   })

  watch(() => windowSizeStore.getWindowSize, () => {
    windowSizeRef.value = { width : windowSizeStore.getWindowSize['width'],
                      height : windowSizeStore.getWindowSize['height'] }
    updateWidthHeights()
  })

  function boardMove(arrayIndex,from,to) {
  }

 function toplineClick(whatClicked) {
    //console.log("topline",whatClicked)
    switch (whatClicked) {
      case "done" :
        processWords("command done")
        break;
      case "microphone" :
        microphoneIsMuted.value = !microphoneIsMuted.value 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(microphoneIsMuted.value)
        break

    }
  }


  // START Explain Tour Routines

  function explainTourInitialize(errorText) {
    showState.value = SHOW_EXPLAINTOUR
    explainTourScreen.value = (errorText == null) ? "lichess_opengames" : ("ERROR:" + errorText);
    wordlist.value = connectionStore.getExplainTourWords
    emit("word-list",wordlist.value)
    wordlistTrigger.value++
  }

  function explainTourToolbarClick(whatClicked) {
    switch (whatClicked) {
      case "mute" :
        microphoneIsMuted.value = true 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(true)
        break
      case "listen" :
        microphoneIsMuted.value = false 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(false)
        break
      case "done" :
        showState.value = SHOW_LIST
        wordlist.value = wordlistOpenGames
        emit("word-list",wordlist.value)
        wordlistTrigger.value++
        break
    }
  }

  // END Explain Tour Routines

  function toolbarClick(arrayIndex) {
    //console.log("toolbar click and show state ",arrayIndex,showState.value)
    switch (arrayIndex) {
      case TOOLBAR_GOTO :
        if (openGameId != "")
            processWords("command go two")
        break
      case TOOLBAR_REFRESH :
        processWords("command refresh")
        break
      case TOOLBAR_EXPLAIN :
        processWords("command explain")
        break;
    }
  }

  function onClick(index) {

    // remove any selected
    for (var i=0; i < gameList.value.length; i++)
      gameList.value[i].selected = false

    gameList.value[index].selected = true
    openGameId = gameList.value[index].gameId
    fen.value = gameList.value[index].fen
    orientation.value = gameList.value[index].color.toLowerCase()

    toolbarElements.value[TOOLBAR_GOTO].disabled = false
    toolbarTrigger.value++
  }

  function parseCommands(words,currentWordIndex) {
    
    //console.log("in parse commands",words,currentWordIndex)
    var currentWord = (words.length > currentWordIndex) ? words[currentWordIndex] : "xyz"
    var nextWord = (words.length > currentWordIndex+1) ? words[currentWordIndex+1] : "xyz"
    var nextNextWord = (words.length > currentWordIndex+2) ? words[currentWordIndex+2] : "xyz"

    for (var i = 0; i < words.length; i++) {
        if (currentWord == "ignore") {
            return ["ignore",""];
        }
        if (currentWord == "explain") {
            return ["explain",""]
        }
        if (currentWord == "done") {
            return ["done",""]
        }
        if (currentWord == "listen") {
            return ["listen",""]
        }
        if (currentWord == "mute") {
            return ["mute",""]
        }
        if (currentWord == "scroll") {
            if (nextWord == "up") {
                return ["scroll", "up"]
            }
            if (nextWord == "down") {
                return ["scroll", "down"]
            }
        }
        
        // add choices here
        if (currentWord == "game") {
          const gameIndex = parseNumberWords(nextWord,nextNextWord)
          return ["game", gameIndex]
        }
       
        if (currentWord == "go" && nextWord == "two") {
          return ["goto",""]
        }

        if (currentWord == "refresh")
          return ["refresh", ""]

        currentWord = nextWord;
        nextWord = nextNextWord;
        nextNextWord = (i < words.length - 3) ? words[i + 3] : "xyz";
    }
    return (["ignore",""]);
  }

  function processWords(text) {

    //console.log("process words",text)
    if (showState.value == SHOW_EXPLAINTOUR) {
        //console.log("*** words for explain tour",text)
        explainTourWordsSpoken.value = text
        explainTourWordsSpokenTrigger.value++
        return
    }
    var returnArray = parseWordsSaid(text,parseCommands)

    switch (returnArray[0]) {
      case "scroll" :
        scrollElement("open-game-list",returnArray[1])
        break
      case "game" :
        const gameIndex = returnArray[1] - 1
        if (gameIndex < 0)
          break
        if (gameIndex > gameList.value.length -1)
          break
        onClick(gameIndex)
        break
      case "refresh" :
        getOpenGames()
        break
      case "goto" :
        //console.log("******** open game id",openGameId)
        if (openGameId != "") {
          connectionStore.setLichessGameId(openGameId + ",/lichessopen")
          emit("where-next","/lichessplay")
         //router.replace("/lichessplay")
        }
        break
      case "explain" :
        explainTourInitialize(null)
        break
      case "mute" :
        microphoneIsMuted.value = true 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(true)
        break
      case "listen" :
        microphoneIsMuted.value = false 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(false)
        break
      case "done" :
        emit("where-next","/lichessmenu")
        //router.replace({ path: "/lichessmenu"})
        break
    }
  }
  
  function processJsonResponse(json) {
    //console.log(" in open games list callback",json)
    if (json.hasOwnProperty('error')) {
      explainTourInitialize(generateErrorAlertText(json))
      return;
    }

    if (json.hasOwnProperty('nowPlaying') == false)
      return
        
    const playArray = json["nowPlaying"]
    for (var i = 0; i < playArray.length; i++) {
      const game = playArray[i]
      
      //console.log("GAME ",i,game)

      // has to be standard variant

      const variant = game["variant"]
      const gameType = variant["name"]
      if (gameType != "Standard")
        continue

      // can only play rapid, classic
      const speed = game["speed"]
      if (speed != "correspondence" &&  speed != "rapid" && speed != "classical")
        continue

      const opponent = game["opponent"]
      //console.log("*** opponent",opponent)
      var opponentName = ""

      if (opponent.hasOwnProperty('ai'))
        opponentName = "ai"
      else
        opponentName = opponent["username"]
        
      const fen = game["fen"]
      const gameId = game["gameId"]
      const rated = game["rated"]
      const isMyTurn = game["isMyTurn"]
      const color = game["color"]

      if (fen == null || gameId == null || rated == null || speed == null || 
                    isMyTurn == null || color == null)
        continue   

      const oneGame = {
        index : i,
        name : opponentName,
        fen : fen,
        gameId : gameId,
        rated : (rated != 0) ? translateEnglishWords("rated") : translateEnglishWords("casual"),
        speed : translateEnglishWords(speed),
        myTurn : (isMyTurn != 0) ? "(*)" : "",
        opponentTurn : (isMyTurn != 0) ? "" : "(*)",
        color : (color == "white") ? translateEnglishWords("White") : translateEnglishWords("Black"),
        selected : false

      }
      gameList.value.push(oneGame)
    }
    showLoadingGames.value = false
  }

  function getOpenGames() {
    gameList.value = []
    toolbarElements.value[TOOLBAR_GOTO].disabled = true
    toolbarTrigger.value++
    openGameId = ""
    showLoadingGames.value = true
    const urlStr = "https://lichess.org/api/account/playing?nb=50"
    getLichessData(urlStr, lichessAccessToken,null, processJsonResponse) 
  }

</script>

<style scoped>

.selected-text-light {
  padding: 2px;
  border : solid 1px black;
  border-radius: 3px;
}

.selected-text-dark {
  padding: 2px;
  border : solid 1px #fff;
  border-radius: 3px;
}
</style>
